import type { StoryCtx } from './ctx.js';
import { displayOf } from './ctx.js';
import { draftDocOf, manuscriptOf, participantsOf, povOf } from './narrative.js';
import { firstPersonNarrator, povAbsent, povAbsentIgnore, povFromText, usesPov, withoutPov } from './perspective.js';
import { mentionsIn, refIndex } from './refs.js';
import { isScene } from './scene.js';
import { stateRoleOf } from './states.js';
import { formatSceneTime } from './sceneTime.js';
import { draftSettingConflicts, thoughtLinksIn } from './settingConflicts.js';
import { holderConflicts, holderIgnore, lifeChangeConflicts, lifeIgnore, lifePovConflict, lifeTextConflicts, type LifeConflict } from './stateConflicts.js';
import { textTimeMismatch } from './textTime.js';
import { heardIn, isTimeConflictIgnored, marksWithText, sceneTimeConflicts, timeConflictIgnore, timeMismatchIgnored, weekdayConflict, wordingFix } from './timeConflicts.js';
import { placeClashes, placeClashIgnore } from './placeClash.js';
import { knowledgeLeaks, neverRevealed, repeatedReveals } from './knowledgeChecks.js';
import { relationFindings, type RelationFinding } from './relationChecks.js';
import { genderOf, relationWord } from './relations.js';
import { termIndex, unlinkedTerms, visibleText, type VisibleText } from './terms.js';
import { ageTextConflicts, type TextRef } from './ageText.js';
import { injuryIgnore, itemUseConflicts, openInjuries, travelConflicts, travelIgnore, traitTextConflicts } from './continuity.js';
import { durationText } from './relations.js';
import { beatFindings, percent } from './beats.js';
import { priorText } from './series.js';
import type { Entity, IgnoredTerm, PriorSource, StateChange, StoryNode, TextDocument } from './types.js';

export type IssueCode = 'unknown-ref' | 'unlinked-mention' | 'unmentioned-link' | 'missing-ref' | 'time-mismatch' | 'time-order' | 'time-wording' | 'place-mismatch' | 'pov-mismatch' | 'pov-hop' | 'pov-absent' | 'pov-missing' | 'life-text' | 'life-pov' | 'life-change' | 'item-holder' | 'time-weekday' | 'place-clash' | 'knowledge-leak' | 'reveal-repeat' | 'never-revealed' | 'relation-age' | 'relation-death' | 'relation-life' | 'relation-apart' | 'age-text' | 'travel-time' | 'injury-open' | 'trait-text' | 'item-use' | 'beat-order' | 'beat-drift' | 'beat-open';

/** Bereich eines Hinweises (Prüfbericht, PF 28): wonach sich die Hinweise filtern lassen */
export const ISSUE_AREAS = ['zeit', 'ort', 'perspektive', 'zustand', 'wissen', 'beziehungen', 'struktur', 'verweise'] as const;
export type IssueArea = (typeof ISSUE_AREAS)[number];

/** Bereich je Art – jede neue Prüfung braucht hier ihren Bereich */
const AREA: Record<IssueCode, IssueArea> = {
  'unknown-ref': 'verweise',
  'unlinked-mention': 'verweise',
  'unmentioned-link': 'verweise',
  'missing-ref': 'verweise',
  'time-mismatch': 'zeit',
  'time-order': 'zeit',
  'time-wording': 'zeit',
  'time-weekday': 'zeit',
  'place-mismatch': 'ort',
  'place-clash': 'ort',
  'pov-mismatch': 'perspektive',
  'pov-hop': 'perspektive',
  'pov-absent': 'perspektive',
  'pov-missing': 'perspektive',
  'life-text': 'zustand',
  'life-pov': 'zustand',
  'life-change': 'zustand',
  'item-holder': 'zustand',
  'knowledge-leak': 'wissen',
  'reveal-repeat': 'wissen',
  'never-revealed': 'wissen',
  'relation-age': 'beziehungen',
  'relation-death': 'beziehungen',
  'relation-life': 'beziehungen',
  'relation-apart': 'beziehungen',
  'age-text': 'zeit',
  'travel-time': 'ort',
  'injury-open': 'zustand',
  'trait-text': 'zustand',
  'item-use': 'zustand',
  'beat-order': 'struktur',
  'beat-drift': 'struktur',
  'beat-open': 'struktur',
};

/** Die Verweise eines sichtbaren Textes mit ihren Schlüsseln */
const textRefsOf = (v: VisibleText): TextRef[] => v.linked.map(([from, to], i) => ({ from, to, key: v.keys[i] ?? '' })).filter((r) => r.key !== '');
export const issueArea = (code: IssueCode): IssueArea => AREA[code];

export interface Issue {
  id: string;
  severity: 'warn' | 'info';
  code: IssueCode;
  /** Node, an dem der Hinweis hängt (dorthin navigiert die Oberfläche); leer: an keinem – dann führt `entityId` (Beziehungen) */
  nodeId: string;
  /** Textdokument mit der Stelle (dorthin führt „Anzeigen“; fehlende Verweise: dort wird korrigiert) */
  docId?: string;
  /** Element, um das es geht (fehlende Verweise) */
  entityId?: string;
  /** Beziehung, um die es geht (AP-44): an ihr zeigt die Seitenleiste der Entität den Hinweis */
  relationId?: string;
  message: string;
  /** „Ist Absicht“ ohne Stelle im Text (Perspektivfigur fehlt): dieser Eintrag, gemerkt in diesem Dokument */
  intended?: { docId: string; entry: IgnoredTerm };
}

/** Sichtbarer Text je Dokument: Dokumente ändern sich nie an Ort und Stelle, ein neuer Stand ist ein neues Objekt. */
const visibleCache = new WeakMap<TextDocument, VisibleText>();
const visibleOf = (doc: TextDocument): VisibleText => {
  let v = visibleCache.get(doc);
  if (!v) visibleCache.set(doc, (v = visibleText(doc.text)));
  return v;
};

const titleOf = (ctx: StoryCtx, n: StoryNode): string => displayOf(ctx, n).title;
/** Wo etwas geschah: in „Szene“ bzw. – aus der Vorgeschichte der Serie – in Episode 2 („Szene“) */
const inScene = (ctx: StoryCtx, sceneId: string | undefined, prior?: PriorSource): string => {
  if (prior) return `in ${priorText(prior)}`;
  const n = sceneId ? ctx.nodes.get(sceneId) : undefined;
  return `in „${n ? titleOf(ctx, n) : ''}“`;
};
const entityName = (ctx: StoryCtx, id: string): string => {
  const e = ctx.entities.get(id);
  return (e && String(e.fields[ctx.types.get(e.typeId)?.titleKey ?? ''] ?? '').trim()) || (e ? `@${e.refKey}` : id);
};

/** Einträge, mit denen ein Node „zusammen auftritt“: über eine Verbindung, weil die Instanz im Node selbst (Ereignis, Zeitraum) oder im selben Elternnode liegt. */
function presentEntities(ctx: StoryCtx, node: StoryNode): { linked: Set<string>; sameScene: Set<string> } {
  const linked = new Set<string>();
  for (const c of ctx.links.get(node.id) ?? []) {
    const other = ctx.nodes.get(c.sourceId === node.id ? c.targetId : c.sourceId);
    if (other?.entityId) linked.add(other.entityId);
  }
  const sameScene = new Set<string>();
  if (node.parentId) for (const s of ctx.children.get(node.parentId) ?? []) if (s.entityId) sameScene.add(s.entityId);
  for (const s of ctx.children.get(node.id) ?? []) if (s.entityId) sameScene.add(s.entityId);
  return { linked, sameScene };
}

/**
 * Hinweise auf Unstimmigkeiten (keine Fehler, nichts wird verhindert):
 *  - unbekannter Verweis `@schlüssel` im Text
 *  - Verweis auf einen Eintrag, der in dieser Szene weder verbunden ist noch vorkommt
 *  - verbundener Eintrag, der im ausgeschriebenen Text nie erwähnt wird (Hinweis)
 *  - fehlende Verweise: Name oder Vorkommen eines Elements steht in einem Text, ist dort aber nicht verknüpft (je Dokument und Element)
 *  - Zeitmarke passt nicht mehr: die Zeitmarken im Draft einer Szene ergeben eine andere Zeit als die, auf der sie steht
 *  - Zeitmarke in falscher Reihenfolge: eine relative Marke zählt ab ihrem Anker, gelesen wird sie nach einer anderen Szene
 *  - Wortlaut einer Zeitmarke geändert: ihr Text sagt etwas anderes als ihr Wert
 *  - Text gegen Angabe von Hand: Schauplatz bzw. Perspektive anders gesetzt, als der Draft sagt (settingConflicts.ts)
 *  - Perspektive (perspective.ts): Head-Hopping, Perspektivfigur nicht in der Szene, Szene ohne Perspektive (Hinweis, mit dem Vorschlag
 *    aus dem Text)
 *  - Zustand (stateConflicts.ts): eine Figur spricht, handelt, denkt, erfährt etwas, hat die Perspektive oder eine Änderung betrifft sie,
 *    obwohl sie zu der Zeit tot bzw. noch nicht geboren ist; wer einen Gegenstand weitergibt bzw. verliert, hat ihn da nicht. In Weltzeit
 *    ein Widerspruch, ohne vergleichbare Zeiten nach der Lesereihenfolge nur ein Hinweis
 *  - Zeit und Ort (AP-40): Wochentag einer Zeitmarke passt nicht zum Datum; eine Figur an zwei Orten zugleich (placeClash.ts)
 *  - Wissen (AP-43, knowledgeChecks.ts): eine Figur benutzt, was sie erst später (bzw. nie) erfährt; eine Enthüllung, die der Leser
 *    schon kennt, und Erfahrenes, das dem Leser nie enthüllt wird (beides Hinweise)
 *  - Beziehungen (AP-44, relationChecks.ts): Elternteil jünger als das Kind, bei der Geburt sehr jung (Hinweis) bzw. schon tot; eine
 *    Beziehung beginnt, wenn einer der beiden nicht lebt; verheiratet, aber nie zusammen in einer Szene (Hinweis)
 * Was im Dokument als Absicht gemerkt ist (timeConflicts.ts), fehlt.
 */
export function checkConsistency(ctx: StoryCtx, opts: { references?: boolean } = {}): Issue[] {
  const refs = refIndex(ctx);
  const terms = termIndex(ctx);
  const withPov = usesPov(ctx);
  const clashes = placeClashes(ctx);
  const travels = travelConflicts(ctx);
  const issues: Issue[] = [];
  for (const node of ctx.story.nodes) {
    const draft = isScene(node) ? draftDocOf(node) : undefined;
    const textTime = textTimeMismatch(ctx, node);
    if (textTime && !timeMismatchIgnored(node, draft?.ignored)) {
      const when = (t: typeof textTime): string => formatSceneTime(t.start, t) + (t.end ? ` – ${formatSceneTime(t.end, t)}` : '');
      issues.push({
        id: `time-mismatch:${node.id}`,
        severity: 'warn',
        code: 'time-mismatch',
        nodeId: node.id,
        ...(draft ? { docId: draft.id } : {}),
        message: `Die Zeitmarken im Text von „${titleOf(ctx, node)}“ ergeben ${when(textTime)}, die Szene steht aber auf ${when(node.time!)}.`,
      });
    }
    if (draft?.text.includes(']{zeit')) {
      const marks = marksWithText(draft.text);
      for (const c of sceneTimeConflicts(ctx, node, marks)) {
        if (c.kind !== 'order' || isTimeConflictIgnored(draft.ignored, timeConflictIgnore(c, node, marks[c.index]!))) continue;
        const anchor = ctx.nodes.get(c.anchorId);
        const previous = c.previousId ? ctx.nodes.get(c.previousId) : undefined;
        issues.push({
          id: `time-order:${node.id}`,
          severity: 'warn',
          code: 'time-order',
          nodeId: node.id,
          docId: draft.id,
          message: `„${marks[c.index]!.text}“ in „${titleOf(ctx, node)}“ zählt ab „${anchor ? titleOf(ctx, anchor) : ''}“, gelesen wird es aber ${previous ? `nach „${titleOf(ctx, previous)}“` : 'vor jeder Szene mit Zeit'}.`,
        });
      }
    }
    // Text gegen Angabe von Hand (PF 56): Schauplatz bzw. Perspektive anders gesetzt, als der Draft sagt; Head-Hopping (PF 26) je Szene einmal
    if (draft && isScene(node)) {
      const name = (id: string): string => entityName(ctx, id);
      const hoppers = new Set<string>();
      let pov = '';
      for (const c of draftSettingConflicts(ctx, node)) {
        if (c.kind === 'hop') {
          for (const id of c.thinkers) hoppers.add(id);
          pov = c.settingId;
          continue;
        }
        issues.push(
          c.kind === 'place'
            ? { id: `place-mismatch:${node.id}`, severity: 'warn', code: 'place-mismatch', nodeId: node.id, docId: draft.id, message: `Der Text von „${titleOf(ctx, node)}“ nennt ${name(c.at.entityId)} als Ort, Schauplatz ist aber ${name(c.settingId)}.` }
            : { id: `pov-mismatch:${node.id}`, severity: 'warn', code: 'pov-mismatch', nodeId: node.id, docId: draft.id, message: `Der Text von „${titleOf(ctx, node)}“ gibt Gedanken von ${c.thinkers.map(name).join(', ')} wieder, Perspektive ist aber ${name(c.settingId)}.` },
        );
      }
      if (hoppers.size > 0) {
        issues.push({
          id: `pov-hop:${node.id}`,
          severity: 'warn',
          code: 'pov-hop',
          nodeId: node.id,
          docId: draft.id,
          message: `„${titleOf(ctx, node)}“ wird aus der Sicht von ${name(pov)} erzählt, gibt aber auch Gedanken von ${[...hoppers].map(name).join(', ')} wieder (Head-Hopping).`,
        });
      }
    }
    // Perspektive (AP-41): die Perspektivfigur kommt nicht vor; geschrieben, aber ohne Perspektive (Hinweis, mit dem Vorschlag aus dem Text)
    if (isScene(node)) {
      const pov = povOf(ctx, node);
      if (pov && draft && povAbsent(ctx, node, terms, refs)) {
        issues.push({
          id: `pov-absent:${node.id}`,
          severity: 'warn',
          code: 'pov-absent',
          nodeId: node.id,
          message: `„${titleOf(ctx, node)}“ wird aus der Sicht von ${entityName(ctx, pov)} erzählt, ${entityName(ctx, pov)} kommt in der Szene aber nicht vor – weder beteiligt noch im Text genannt.`,
          intended: { docId: draft.id, entry: povAbsentIgnore(ctx, pov) },
        });
      }
      if (!pov && withPov && draft && draft.text.trim() !== '' && !withoutPov(draft.ignored)) {
        const suggested = povFromText(ctx, node, thoughtLinksIn(ctx, draft.text), draft.ignored);
        // Ich-Form (PF 50): der Ich-Erzähler der übrigen Szenen
        const narrator = suggested ? null : firstPersonNarrator(ctx, node, draft.ignored);
        issues.push({
          id: `pov-missing:${node.id}`,
          severity: 'info',
          code: 'pov-missing',
          nodeId: node.id,
          ...(suggested || narrator ? { docId: draft.id } : {}),
          message: `„${titleOf(ctx, node)}“ hat keine Perspektive.${suggested ? ` Der Text gibt nur Gedanken von ${entityName(ctx, suggested.entityId)} wieder.` : narrator ? ` Sie ist in der Ich-Form erzählt – wie die Szenen aus der Sicht von ${entityName(ctx, narrator)}.` : ''}`,
        });
      }
    }
    // Wissen (AP-43): Wissenslecks im Draft; Enthüllungen, die der Leser schon kennt
    if (isScene(node) && draft) {
      const title = titleOf(ctx, node);
      const sceneName = (id: string | null): string => {
        const n = id ? ctx.nodes.get(id) : undefined;
        return n ? titleOf(ctx, n) : '';
      };
      for (const l of knowledgeLeaks(ctx, node)) {
        const uses = l.role === 'gedanke' ? `denkt ${entityName(ctx, l.knower)} an` : `spricht ${entityName(ctx, l.knower)} von`;
        const when = l.certainty === 'never' ? 'erfährt es aber nirgends' : `erfährt es ${l.certainty === 'likely' ? 'nach der Lesereihenfolge ' : ''}aber erst in „${sceneName(l.learnedIn)}“`;
        issues.push({
          id: `knowledge-leak:${node.id}:${l.knower}:${l.about}`,
          severity: l.certainty === 'sure' ? 'warn' : 'info',
          code: 'knowledge-leak',
          nodeId: node.id,
          docId: draft.id,
          entityId: l.knower,
          message: `In „${title}“ ${uses} „${entityName(ctx, l.about)}“ – ${when}.`,
        });
      }
      for (const r of repeatedReveals(ctx, node)) {
        issues.push({
          id: `reveal-repeat:${node.id}:${r.about}`,
          severity: 'info',
          code: 'reveal-repeat',
          nodeId: node.id,
          docId: r.docId,
          entityId: r.about,
          message: `In „${title}“ wird „${entityName(ctx, r.about)}“ enthüllt – der Leser kennt es schon aus ${r.prior ? priorText(r.prior) : `„${sceneName(r.firstSceneId)}“`}.`,
        });
      }
    }
    // Zwei Orte zugleich (AP-40): je Figur und andere Szene ein Widerspruch, „ist Absicht“ im Draft dieser Szene
    if (isScene(node) && draft) {
      for (const c of clashes.get(node.id) ?? []) {
        const other = ctx.nodes.get(c.otherId)!;
        issues.push({
          id: `place-clash:${node.id}:${c.entityId}:${c.otherId}`,
          severity: 'warn',
          code: 'place-clash',
          nodeId: node.id,
          entityId: c.entityId,
          message: `${entityName(ctx, c.entityId)} ist in „${titleOf(ctx, node)}“ in ${entityName(ctx, c.place)} – und zur selben Zeit in „${titleOf(ctx, other)}“ in ${entityName(ctx, c.otherPlace)}.`,
          intended: { docId: draft.id, entry: placeClashIgnore(ctx, c) },
        });
      }
    }
    // Reisezeit (AP-40): von der vorigen Szene der Figur an einem anderen Ort bleibt zu wenig Zeit für den Weg
    if (isScene(node) && draft) {
      for (const c of travels.get(node.id) ?? []) {
        const from = ctx.nodes.get(c.fromSceneId)!;
        issues.push({
          id: `travel-time:${node.id}:${c.entityId}:${c.fromSceneId}`,
          severity: 'warn',
          code: 'travel-time',
          nodeId: node.id,
          entityId: c.entityId,
          message: `${entityName(ctx, c.entityId)} ist in „${titleOf(ctx, from)}“ in ${entityName(ctx, c.fromPlace)} und danach in „${titleOf(ctx, node)}“ in ${entityName(ctx, c.place)} – dazwischen liegen höchstens ${durationText(c.gap)}, der Weg dauert ${durationText(c.need)}${durationText(c.need).endsWith('.') ? '' : '.'}`,
          intended: { docId: draft.id, entry: travelIgnore(ctx, c) },
        });
      }
    }
    // Zustand (AP-42): lebt die Figur hier? Hat, wer etwas weitergibt, es da?
    if (isScene(node) && draft) {
      const title = titleOf(ctx, node);
      const name = (id: string | null): string => (id ? entityName(ctx, id) : '');
      const sceneName = (id: string): string => {
        const n = ctx.nodes.get(id);
        return n ? titleOf(ctx, n) : '';
      };
      const note = (c: LifeConflict): string =>
        (c.state === 'dead' ? `zu der Zeit schon tot (gestorben ${inScene(ctx, c.eventSceneId, c.prior)})` : `zu der Zeit noch nicht geboren (geboren ${inScene(ctx, c.eventSceneId, c.prior)})`) +
        (c.certainty === 'likely' ? (c.prior ? '. Nach der Reihenfolge der Episoden – die Szenen haben keine vergleichbare Zeit' : '. Nach der Lesereihenfolge – die Szenen haben keine vergleichbare Zeit') : '');
      /** seit wann ein Wert gilt: seit einer Szene, seit einer früheren Episode bzw. `start` (von Anfang an) */
      const since = (sceneId: string, prior: PriorSource | undefined, start: string): string =>
        sceneId ? `seit „${sceneName(sceneId)}“` : prior ? `seit ${priorText(prior)}` : start;
      const severity = (c: { certainty: string }): Issue['severity'] => (c.certainty === 'sure' ? 'warn' : 'info');
      const intended = (c: { certainty: string }, entry: IgnoredTerm): Pick<Issue, 'intended'> => (c.certainty === 'sure' ? { intended: { docId: draft.id, entry } } : {});
      for (const c of lifeTextConflicts(ctx, node, draft.text, draft.ignored, refs)) {
        const verb = { rede: 'spricht', handlung: 'handelt', gedanke: 'denkt', erfährt: 'erfährt' }[c.role as string] ?? 'tritt auf';
        issues.push({ id: `life-text:${node.id}:${c.entityId}`, severity: severity(c), code: 'life-text', nodeId: node.id, docId: draft.id, entityId: c.entityId, message: `In „${title}“ ${verb} ${name(c.entityId)}${c.role === 'erfährt' ? ' etwas' : ''} – ${note(c)}.` });
      }
      const pov = lifePovConflict(ctx, node, draft.ignored);
      if (pov) {
        issues.push({ id: `life-pov:${node.id}`, severity: severity(pov), code: 'life-pov', nodeId: node.id, entityId: pov.entityId, message: `„${title}“ wird aus der Sicht von ${name(pov.entityId)} erzählt – ${note(pov)}.`, ...intended(pov, lifeIgnore(ctx, pov.entityId, 'perspektive')) });
      }
      const what = (c: StateChange, id: string): string =>
        c.kind === 'besitz' && c.value === id
          ? `bekommt ${name(id)} ${name(c.entityId)}`
          : c.kind === 'besitz'
            ? c.value === null
              ? `verliert ${name(id)} ${name(c.entityId)}`
              : `gibt ${name(id)} ${name(c.entityId)} weiter`
            : c.kind === 'verfassung'
              ? c.value === 'tot'
                ? `stirbt ${name(id)}`
                : c.value === 'geboren'
                  ? `wird ${name(id)} geboren`
                  : `ist ${name(id)} ${c.value}`
              : `wechselt ${name(id)} den Ort (${name(c.value)})`;
      for (const c of lifeChangeConflicts(ctx, node, draft.ignored)) {
        issues.push({ id: `life-change:${node.id}:${c.entityId}`, severity: severity(c), code: 'life-change', nodeId: node.id, entityId: c.entityId, message: `In „${title}“ ${what(c.change, c.entityId)} – ${note(c)}.`, ...intended(c, lifeIgnore(ctx, c.entityId, 'änderung')) });
      }
      for (const c of holderConflicts(ctx, node, draft.ignored)) {
        const at = c.holder === null ? 'verloren' : stateRoleOf(ctx, c.holder) === 'place' ? `in ${name(c.holder)}` : `bei ${name(c.holder)}`;
        issues.push({
          id: `item-holder:${node.id}:${c.change.id}`,
          severity: severity(c),
          code: 'item-holder',
          nodeId: node.id,
          entityId: c.change.entityId,
          message: `In „${title}“ ${c.change.value === null ? 'verliert' : 'gibt'} ${name(c.change.from)} ${name(c.change.entityId)}${c.change.value === null ? '' : ' weiter'} – zu der Zeit ist ${name(c.change.entityId)} aber ${at} (${since(c.holderSceneId, c.prior, 'laut Feld „Besitzer“')})${c.certainty === 'likely' ? '. Nach der Lesereihenfolge – die Szenen haben keine vergleichbare Zeit' : ''}.`,
          ...intended(c, holderIgnore(ctx, c.change)),
        });
      }
      // Merkmal im Text (AP-42): eine Ausprägung, die zu der Zeit nicht gilt – rot an der Stelle
      const visible = visibleOf(draft);
      const textRefs = textRefsOf(visible);
      for (const c of traitTextConflicts(ctx, node, visible.text, textRefs, draft.ignored, refs)) {
        issues.push({
          id: `trait-text:${node.id}:${c.entityId}:${c.said.toLowerCase()}`,
          severity: 'warn',
          code: 'trait-text',
          nodeId: node.id,
          docId: draft.id,
          entityId: c.entityId,
          message: `In „${title}“ steht bei ${name(c.entityId)} „${c.said}“ – zu der Zeit gilt aber ${c.trait}: „${c.actual}“ (${since(c.sinceSceneId, c.prior, 'von Anfang an')}).`,
        });
      }
      // Benutzter Gegenstand (AP-42): eine Handlung mit einem Gegenstand, den die Figur da nicht hat
      for (const c of itemUseConflicts(ctx, node, draft.text, draft.ignored)) {
        const where = c.holder === null ? 'verloren' : stateRoleOf(ctx, c.holder) === 'place' ? `in ${name(c.holder)}` : `bei ${name(c.holder)}`;
        issues.push({
          id: `item-use:${node.id}:${c.personId}:${c.itemId}`,
          severity: 'warn',
          code: 'item-use',
          nodeId: node.id,
          docId: draft.id,
          entityId: c.itemId,
          message: `In „${title}“ benutzt ${name(c.personId)} ${name(c.itemId)} – zu der Zeit ist ${name(c.itemId)} aber ${where} (${since(c.sinceSceneId, c.prior, 'laut Feld „Besitzer“')}).`,
        });
      }
      // Alter im Text gegen die Lebensdaten (AP-40): rot an der Stelle, „ist Absicht“ dort
      for (const c of ageTextConflicts(ctx, node, visible.text, textRefs, draft.ignored, refs)) {
        issues.push({
          id: `age-text:${node.id}:${c.entityId}:${c.years}`,
          severity: 'warn',
          code: 'age-text',
          nodeId: node.id,
          docId: draft.id,
          entityId: c.entityId,
          message: `In „${title}“ ist ${name(c.entityId)} ${c.years} Jahre alt – nach der Geburt (${c.bornPrior ? priorText(c.bornPrior) : `„${sceneName(c.bornSceneId)}“`}) aber ${c.actual}.`,
        });
      }
    }
    for (const doc of node.documents) {
      if (!doc.text.includes(']{zeit')) continue;
      for (const mark of marksWithText(doc.text)) {
        const fix = wordingFix(mark, heardIn(mark.text));
        if (!fix || isTimeConflictIgnored(doc.ignored, timeConflictIgnore({ kind: 'wording', index: 0, fix }, node, mark))) continue;
        issues.push({
          id: `time-wording:${node.id}:${doc.id}:${mark.text}`,
          severity: 'warn',
          code: 'time-wording',
          nodeId: node.id,
          docId: doc.id,
          message: `Die Zeitmarke „${mark.text}“ in „${titleOf(ctx, node)}“ sagt etwas anderes als ihre Zeit – der Text wurde geändert, die Zeit nicht.`,
        });
      }
      // Wochentag gegen Datum (AP-40)
      for (const mark of marksWithText(doc.text)) {
        const wd = weekdayConflict(mark);
        if (!wd || isTimeConflictIgnored(doc.ignored, timeConflictIgnore({ kind: 'weekday', index: 0, ...wd }, node, mark))) continue;
        issues.push({
          id: `time-weekday:${node.id}:${doc.id}:${mark.text}`,
          severity: 'warn',
          code: 'time-weekday',
          nodeId: node.id,
          docId: doc.id,
          message: `„${mark.text}“ in „${titleOf(ctx, node)}“: Das Datum fällt auf einen ${wd.actual}, nicht auf einen ${wd.said}.`,
        });
      }
    }
    // Verweise und Vorkommen ändern sich nicht, wenn eine Szene in der Zeit oder im Manuskript wandert (consequences.ts): dann ohne sie
    if (node.documents.length === 0 || opts.references === false) continue;

    // Szenen sind Erzähltext (dort erwähnt man auch Abwesende); für sie gilt nur der unbekannte Verweis
    const narrative = isScene(node);
    const { linked, sameScene } = presentEntities(ctx, node);
    const mentioned = new Set<string>();
    const reported = new Set<string>();
    for (const block of node.documents) {
      for (const m of mentionsIn(block.text)) {
        const entity = refs.get(m.key);
        if (!entity) {
          if (!reported.has(`u:${m.key}`)) {
            reported.add(`u:${m.key}`);
            issues.push({ id: `unknown-ref:${node.id}:${m.key}`, severity: 'warn', code: 'unknown-ref', nodeId: node.id, docId: block.id, message: `„${titleOf(ctx, node)}“ verweist auf @${m.key}, aber es gibt keine Entität mit diesem Schlüssel.` });
          }
          continue;
        }
        mentioned.add(entity.id);
        if (narrative) continue;
        const type = ctx.types.get(entity.typeId);
        if (type?.category === 'foundational') continue; // mit allem unsichtbar verknüpft
        if (!linked.has(entity.id) && !sameScene.has(entity.id) && !reported.has(`l:${entity.id}`)) {
          reported.add(`l:${entity.id}`);
          issues.push({
            id: `unlinked-mention:${node.id}:${entity.id}`,
            severity: 'warn',
            code: 'unlinked-mention',
            nodeId: node.id,
            docId: block.id,
            message: `„${titleOf(ctx, node)}“ erwähnt @${entity.refKey}, aber die Entität ist hier weder verbunden noch in derselben Szene.`,
          });
        }
      }
    }
    for (const doc of node.documents) {
      const count = new Map<string, number>();
      for (const hit of unlinkedTerms(doc, terms, visibleOf(doc))) for (const id of hit.entityIds) count.set(id, (count.get(id) ?? 0) + 1);
      for (const [entityId, n] of count) {
        issues.push({
          id: `missing-ref:${node.id}:${doc.id}:${entityId}`,
          severity: 'info',
          code: 'missing-ref',
          nodeId: node.id,
          docId: doc.id,
          entityId,
          message: `Ich habe fehlende Referenzen der Entität „${entityName(ctx, entityId)}“ im Text „${doc.name}“ (${titleOf(ctx, node)}) gefunden: ${n === 1 ? 'eine Stelle' : `${n} Stellen`}.`,
        });
      }
    }
    const written = node.documents.some((b) => b.text.trim() !== '');
    if (written && !narrative) {
      for (const id of linked) {
        const entity = ctx.entities.get(id);
        if (entity && !mentioned.has(id)) {
          issues.push({ id: `unmentioned-link:${node.id}:${id}`, severity: 'info', code: 'unmentioned-link', nodeId: node.id, message: `„${titleOf(ctx, node)}“ ist mit @${entity.refKey} verbunden, erwähnt ihn aber nicht.` });
        }
      }
    }
  }
  // Wissen (AP-43): Erfahrenes, das dem Leser nie enthüllt wird – an der Szene, in der es zuerst eine Figur erfährt
  for (const u of neverRevealed(ctx)) {
    const scene = ctx.nodes.get(u.sceneId)!;
    issues.push({
      id: `never-revealed:${u.about}`,
      severity: 'info',
      code: 'never-revealed',
      nodeId: u.sceneId,
      docId: u.docId,
      entityId: u.about,
      message: `„${entityName(ctx, u.about)}“ erfährt eine Figur in „${titleOf(ctx, scene)}“ – dem Leser wird es nie enthüllt.`,
    });
  }
  // Verletzung ohne Heilung (AP-42): ein Hinweis an der Szene der Verletzung
  for (const o of openInjuries(ctx)) {
    const scene = ctx.nodes.get(o.sceneId)!;
    const draft = draftDocOf(scene);
    issues.push({
      id: `injury-open:${o.sceneId}:${o.entityId}`,
      severity: 'info',
      code: 'injury-open',
      nodeId: o.sceneId,
      entityId: o.entityId,
      message: `${entityName(ctx, o.entityId)} wird in „${titleOf(ctx, scene)}“ verletzt und tritt danach noch in ${o.later === 1 ? 'einer Szene' : `${o.later} Szenen`} auf – wird aber nie wieder gesund.`,
      ...(draft ? { intended: { docId: draft.id, entry: injuryIgnore(ctx, o.entityId) } } : {}),
    });
  }
  for (const f of relationFindings(ctx)) issues.push(relationIssue(ctx, f));
  // Story Beats (PLAN.md §42): nur Hinweise – die Vorlage ist ein Maßstab, keine Regel
  const manuscript = manuscriptOf(ctx);
  for (const f of beatFindings(ctx)) {
    const name = `„${f.beat.title}“ (${f.sheet.title})`;
    const id = `${f.sheet.id}:${f.beat.id}`;
    if (f.kind === 'order') {
      issues.push({ id: `beat-order:${id}`, severity: 'info', code: 'beat-order', nodeId: f.sceneId, message: `${name} steht in der Lesereihenfolge vor „${f.other.title}“, der in der Liste davor kommt.` });
    } else if (f.kind === 'drift') {
      issues.push({ id: `beat-drift:${id}`, severity: 'info', code: 'beat-drift', nodeId: f.sceneId, message: `${name} liegt bei ${percent(f.at)} des Manuskripts – vorgesehen sind etwa ${percent(f.target)}.` });
    } else if (manuscript) {
      issues.push({ id: `beat-open:${id}`, severity: 'info', code: 'beat-open', nodeId: manuscript.id, message: `Der Beat ${name} hat noch keine Szene – „${f.later.title}“ danach schon.` });
    }
  }
  return issues;
}

const RELATION_CODE: Record<RelationFinding['kind'], IssueCode> = {
  'parent-younger': 'relation-age',
  'parent-young': 'relation-age',
  'parent-dead': 'relation-death',
  'relation-life': 'relation-life',
  'married-apart': 'relation-apart',
};

/** Hinweis zu einem Befund über eine Beziehung: sicher ein Widerspruch, nach der Lesereihenfolge bzw. als Frage ein Hinweis */
function relationIssue(ctx: StoryCtx, f: RelationFinding): Issue {
  const name = (id: string): string => entityName(ctx, id);
  const scene = (id: string | undefined): string => {
    const n = id ? ctx.nodes.get(id) : undefined;
    return n ? titleOf(ctx, n) : '';
  };
  const target = f.relation.targetId;
  const likely = f.certainty === 'likely' && f.kind !== 'parent-young' && f.kind !== 'married-apart' ? ' Nach der Lesereihenfolge – die Szenen haben keine vergleichbare Zeit.' : '';
  // „Mutter von“, „verheiratet mit“: aus Sicht der Quelle, nach ihrem Geschlecht
  const word = relationWord(f.relation.kind, 'source', genderOf(ctx, f.sourceId));
  const message = {
    'parent-younger': () => `${name(f.sourceId)} ist als ${word.replace(/ von$/, '')} von ${name(target)} eingetragen, wird aber erst nach ${name(target)} geboren (in „${scene(f.sceneId!)}“, ${name(target)} in „${scene(f.eventSceneId)}“).`,
    'parent-young': () => `${name(f.sourceId)} ist bei der Geburt von ${name(target)} erst ${f.years === 1 ? 'ein Jahr' : `${f.years} Jahre`} alt – stimmt das?`,
    'parent-dead': () => `${name(f.sourceId)} ist bei der Geburt von ${name(target)} schon tot (gestorben ${inScene(ctx, f.eventSceneId, f.prior)}, geboren in „${scene(f.sceneId!)}“).`,
    'relation-life': () =>
      `„${name(f.sourceId)}, ${word} ${name(target)}“ beginnt in „${scene(f.sceneId!)}“ – da ist ${name(f.entityId)} ${f.state === 'dead' ? `schon tot (gestorben ${inScene(ctx, f.eventSceneId, f.prior)})` : `noch nicht geboren (geboren ${inScene(ctx, f.eventSceneId, f.prior)})`}.`,
    'married-apart': () => `${name(f.sourceId)} und ${name(target)} sind verheiratet, treten aber nie gemeinsam in einer Szene auf – gewollt?`,
  }[f.kind]();
  const severity: Issue['severity'] = f.certainty === 'sure' && f.kind !== 'parent-young' && f.kind !== 'married-apart' ? 'warn' : 'info';
  return {
    id: `${RELATION_CODE[f.kind]}:${f.relation.id}:${f.kind}:${f.entityId}`,
    severity,
    code: RELATION_CODE[f.kind],
    nodeId: f.sceneId ?? '',
    entityId: f.entityId,
    relationId: f.relation.id,
    message: message + likely,
  };
}

/**
 * Einträge, auf die sich ein Text an diesem Node beziehen kann: verbundene, in derselben Szene vorkommende und die Foundationals.
 * Bei einer Szene: ihre Perspektive und ihre Besetzung (Personen in ihr und mit ihr verbundene).
 */
export function entitiesAround(ctx: StoryCtx, node: StoryNode): Entity[] {
  let ids: string[];
  if (isScene(node)) {
    const pov = povOf(ctx, node);
    ids = [...new Set([...(pov ? [pov] : []), ...participantsOf(ctx, node.id)])];
  } else {
    const { linked, sameScene } = presentEntities(ctx, node);
    ids = [...new Set([...linked, ...sameScene])];
  }
  const list = ids.map((id) => ctx.entities.get(id)).filter((e): e is Entity => !!e);
  return [...list, ...ctx.foundationals];
}
